import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Zap, Check, RefreshCw } from 'lucide-react';

interface Message {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
}

// Conversation State Machine
type ChatState = 
  | 'INITIAL' 
  | 'QUALIFYING' 
  | 'CAPTURING_NAME' 
  | 'CAPTURING_PHONE' 
  | 'COMPLETED';

type ServiceType = 'roofing' | 'hvac' | 'plumbing' | 'barber' | 'gym' | 'daycare' | null;

// Mock Lead Capture Analytics
const logLeadCapture = (data: any) => {
  console.log("LEAD CAPTURED:", data);
  // In a real app, this would send data to the backend/CRM
};

export const AIWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      type: 'bot',
      content: "👋 Hi! I'm your North Forge AI receptionist. I can quote jobs, check availability, and book appointments 24/7.\n\nWhat kind of service do you need help with today?",
      timestamp: new Date()
    }
  ]);
  const [currentInput, setCurrentInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [chatState, setChatState] = useState<ChatState>('INITIAL');
  const [serviceType, setServiceType] = useState<ServiceType>(null);
  const [leadData, setLeadData] = useState({ name: '', phone: '' });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const quickReplies = {
    INITIAL: [
      { label: "Roofing Repair", value: "I need roofing repair" },
      { label: "HVAC Service", value: "My AC isn't working" },
      { label: "Plumbing Issue", value: "I have a plumbing emergency" },
      { label: "Barbershop", value: "I need a haircut" },
      { label: "Gym Membership", value: "I want to join the gym" },
      { label: "Daycare", value: "Looking for child care" }
    ],
    QUALIFYING: [
      { label: "Yes, it's urgent", value: "Yes, it is urgent." },
      { label: "Just a quote", value: "I'm just looking for a quote." }
    ],
    CAPTURING_NAME: [], // Free text input
    CAPTURING_PHONE: [], // Free text input
    COMPLETED: [
      { label: "Book Another", value: "Start over" }
    ]
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const addMessage = (content: string, type: 'user' | 'bot') => {
    const newMessage: Message = {
      id: crypto.randomUUID(),
      type,
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const resetChat = () => {
    setMessages([{
        id: crypto.randomUUID(),
        type: 'bot',
        content: "👋 Welcome back! What kind of service do you need help with?",
        timestamp: new Date()
    }]);
    setChatState('INITIAL');
    setServiceType(null);
    setLeadData({ name: '', phone: '' });
    setIsTyping(false);
  };

  const handleUserResponse = async (text: string) => {
    if (!text.trim()) return;

    // User Message
    addMessage(text, 'user');
    setCurrentInput('');
    setIsTyping(true);

    // Smart Delay: 20ms per character + base delay
    const typingDelay = Math.min(2000, 500 + (text.length * 20)); 
    await new Promise(resolve => setTimeout(resolve, typingDelay));

    // Bot Logic
    let response = "";
    let nextState = chatState;

    switch (chatState) {
      case 'INITIAL':
        if (text.toLowerCase().includes('roof')) {
          setServiceType('roofing');
          response = "I can help with that. Is your roof currently leaking, or is this for a future repair?";
          nextState = 'QUALIFYING';
        } else if (text.toLowerCase().includes('ac') || text.toLowerCase().includes('hvac')) {
          setServiceType('hvac');
          response = "Got it. Is your system completely down, or just not cooling well?";
          nextState = 'QUALIFYING';
        } else if (text.toLowerCase().includes('plumb') || text.toLowerCase().includes('leak')) {
          setServiceType('plumbing');
          response = "Understood. Is this an emergency that requires immediate dispatch?";
          nextState = 'QUALIFYING';
        } else if (text.toLowerCase().includes('hair') || text.toLowerCase().includes('barber')) {
          setServiceType('barber');
          response = "Looking sharp! Are you looking for a Fade, a Standard Cut, or a Beard Trim today?";
          nextState = 'QUALIFYING';
        } else if (text.toLowerCase().includes('gym') || text.toLowerCase().includes('fitness')) {
          setServiceType('gym');
          response = "Great goal! Are you interested in a day pass, or looking to sign up for a monthly membership?";
          nextState = 'QUALIFYING';
        } else if (text.toLowerCase().includes('daycare') || text.toLowerCase().includes('child')) {
          setServiceType('daycare');
          response = "We'd love to care for your little one. What age group are you looking for: Infant, Toddler, or Pre-K?";
          nextState = 'QUALIFYING';
        } else {
          response = "I specialize in various local services. Could you clarify which one you need?";
        }
        break;

      case 'QUALIFYING':
        // Transition to lead capture regardless of answer (demo flow)
        response = "Okay, I can help get you set up with that. What is your full name?";
        nextState = 'CAPTURING_NAME';
        break;

      case 'CAPTURING_NAME':
        setLeadData(prev => ({ ...prev, name: text }));
        response = `Thanks, ${text.split(' ')[0]}. What is the best phone number to reach you at?`;
        nextState = 'CAPTURING_PHONE';
        break;

      case 'CAPTURING_PHONE':
        setLeadData(prev => ({ ...prev, phone: text }));
        logLeadCapture({ ...leadData, phone: text, service: serviceType });
        response = "Perfect. I've sent your details to our team. They will contact you shortly to confirm the appointment.\n\nType 'Start over' or click the refresh button to book another service.";
        nextState = 'COMPLETED';
        break;

      case 'COMPLETED':
        if (text.toLowerCase().includes('start')) {
            resetChat();
            return;
        }
        response = "Our team is reviewing your request. Is there anything else I can add to the notes?";
        break;
    }

    setChatState(nextState);
    if (response) {
      addMessage(response, 'bot');
    }
    setIsTyping(false);
  };

  return (
    <>
      {/* Floating Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-brand-primary rounded-full shadow-2xl shadow-brand-primary/30 flex items-center justify-center text-white z-50"
        aria-label="Chat with AI Assistant"
      >
        <MessageCircle className="w-6 h-6" />
        {/* Notification Dot */}
        {!isOpen && messages.length === 1 && (
            <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-brand-dark"></span>
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] h-[550px] bg-brand-dark border border-brand-border rounded-2xl shadow-2xl overflow-hidden z-50 flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-brand-base/90 backdrop-blur-md border-b border-brand-border p-4 flex items-center justify-between z-10">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center relative">
                    <Zap className="w-5 h-5 text-white" />
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-brand-primary rounded-full"></span>
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-sm">North Forge AI</h3>
                    <p className="text-xs text-green-400 font-medium flex items-center gap-1">
                         <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"/> 
                         Online Now
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={resetChat}
                        className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        title="Start New Chat"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        aria-label="Close chat"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-brand-dark/50">
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        message.type === 'user'
                          ? 'bg-brand-primary text-white rounded-tr-none'
                          : 'bg-brand-base border border-brand-border text-slate-200 rounded-tl-none'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>
                      <p className={`text-[10px] mt-1 opacity-50 ${message.type === 'user' ? 'text-right' : 'text-left'}`}>
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </motion.div>
                ))}

                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="bg-brand-base border border-brand-border px-4 py-3 rounded-2xl rounded-tl-none text-slate-300">
                      <div className="flex space-x-1">
                        <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Quick Replies */}
              {!isTyping && quickReplies[chatState] && quickReplies[chatState].length > 0 && (
                 <div className="px-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
                    {quickReplies[chatState].map((reply, idx) => (
                        <button
                            key={idx}
                            onClick={() => handleUserResponse(reply.value)}
                            className="whitespace-nowrap px-3 py-1.5 bg-brand-primary/10 border border-brand-primary/30 rounded-full text-brand-primary text-xs font-medium hover:bg-brand-primary hover:text-white transition-all"
                        >
                            {reply.label}
                        </button>
                    ))}
                 </div>
              )}

              {/* Input Area */}
              <div className="border-t border-brand-border p-4 bg-brand-base/30">
                <div className="flex items-end space-x-2">
                  <textarea
                    value={currentInput}
                    onChange={(e) => setCurrentInput(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleUserResponse(currentInput);
                        }
                    }}
                    placeholder={chatState === 'CAPTURING_NAME' ? "Enter your name..." : chatState === 'CAPTURING_PHONE' ? "Enter your phone number..." : "Type a message..."}
                    className="flex-1 bg-brand-dark/50 border border-brand-border rounded-xl px-4 py-3 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary resize-none h-[46px] max-h-24 custom-scrollbar"
                    style={{ minHeight: '46px' }}
                  />
                  <button
                    onClick={() => handleUserResponse(currentInput)}
                    disabled={!currentInput.trim() || isTyping}
                    className="w-[46px] h-[46px] bg-brand-primary rounded-xl flex items-center justify-center text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-primary/90 transition-all shadow-lg shadow-brand-primary/20"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
                {chatState === 'COMPLETED' && (
                    <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 flex items-center justify-center gap-1.5 text-xs text-green-500"
                    >
                        <Check className="w-3 h-3" />
                        Capture Complete
                    </motion.div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
